<template>
  <div>
    <v-tooltip :disabled="!(mini || !currentRecording)" bottom>
      <template v-slot:activator="{ on }">
        <span v-on="on">
          <v-btn
            class="px-4"
            :disabled="!currentRecording"
            color="#eeeeee"
            elevation="0"
            height="32"
            @click.exact="openOverlay"
          >
            <p v-if="!mini" class="mb-0">{{ $t("Share") }}</p>
            <v-icon
              size="16"
              class="mb-1"
              :class="{ 'ml-2': !mini }"
              color="grey darken-2"
            >
              fa-share-square
            </v-icon>
          </v-btn>
        </span>
      </template>
      <span>{{
        !!currentRecording
          ? $t("Share Footage")
          : $t("Please select a valid recording")
      }}</span>
    </v-tooltip>
    <v-dialog no-click-animation width="600px" v-model="showDialog">
      <v-card v-if="showDialog">
        <v-card-text class="px-5 pt-5">
          <p class="pb-0 ml-1 mb-2">
            {{ $t("Share the recording by copying the link") }}:
          </p>
          <v-text-field
            class="linkTextField"
            v-model="shareLink"
            solo
            flat
            type="text"
            background-color="shadow"
            readonly
            hide-details
          >
            <template v-slot:append>
              <v-row class="pl-3">
                <v-divider vertical></v-divider>
                <v-btn
                  text
                  color="primary"
                  class="mx-1"
                  @click="copyLink(shareLink)"
                >
                  <span v-if="!copied">
                    {{ $t("Copy") }}
                  </span>
                  <span v-else>
                    <v-icon color="primary" class="mx-3" small right
                      >fa-check</v-icon
                    >
                  </span>
                </v-btn>
              </v-row>
            </template>
          </v-text-field>
          <p class="pt-4 ml-1">{{ $t("Or email the link to someone") }}:</p>
          <v-btn :href="emailTo" color="shadow" class="px-8 py-6" elevation="0">
            <span class="primary--text">{{ $t("Email") }}</span>
            <v-icon color="primary" class="pb-1 ml-4" small right
              >fa-envelope-open-text</v-icon
            >
          </v-btn>
        </v-card-text>
        <v-card-actions>
          <p class="ml-4 grey--text">
            {{
              $t("You need to have access to this user to view this footage.")
            }}
          </p>
          <v-spacer></v-spacer>
          <v-btn class="mr-3 mb-3" depressed @click="closeOverlay">{{
            $t("Close")
          }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  reactive,
  toRefs,
  computed,
  watch,
  onBeforeUnmount
} from "../../plugins/compositionApi";
import { t } from "../../service/locale";

export default {
  name: "ShareVideoDialog",
  props: {
    camera: {
      type: Object
    },
    currentRecording: Object,
    currentTime: Number,
    timeLevel: Number,
    eventFilter: Array,
    mini: Boolean
  },
  setup(props, context) {
    const data = reactive({
      copied: false,
      showDialog: false,
      emailSubject: t("Interesting video recording"),
      emailGreetings: t("Hi"),
      emailBody1: t(
        "This link contains an interesting video clip from my video surveillance camera"
      ),
      emailBody2: t(
        "Signing in to this account is required to watch the video in the link."
      ),
      emailRegards: t("Best Regards"),
      sharedCurrentTime: computed(() =>
        props.currentTime ? parseInt(props.currentTime) : new Date().getTime()
      ),
      selectedTime: null,
      sharedTimeLevel: computed(() =>
        props.timeLevel ? parseInt(props.timeLevel) : 3
      ),
      sharedEventFilter: computed(() =>
        props.eventFilter ? props.eventFilter.join() : ""
      ),
      shareLink: computed(
        () =>
          `${window.location.href}?time=${data.selectedTime}&zoom=${data.sharedTimeLevel}&events=${data.sharedEventFilter}`
      ),
      emailTo: computed(
        () =>
          `mailto:?subject=${data.emailSubject}&body=${
            data.emailGreetings
          },%0D%0D${data.emailBody1}:%0D${data.shareLink.replace(
            /&/g,
            "%26"
          )}%0D%0D${data.emailBody2}%0D%0D${data.emailRegards}`
      )
    });

    const { openOverlay, closeOverlay } = useOverlay(data, context);
    const { copyLink } = useSharing(data);

    return { ...toRefs(data), openOverlay, closeOverlay, copyLink };
  }
};

function useSharing(data) {
  function fallbackCopyLink() {
    let textArea = document.querySelector(".linkTextField");
    textArea.focus();
    textArea.select();
    try {
      let successful = document.execCommand("copy");
      data.copied = !!successful;
    } catch (err) {
      throw err;
    }
  }

  function copyLink(text) {
    if (!navigator.clipboard) {
      fallbackCopyTextToClipboard(text);
      return;
    }
    navigator.clipboard.writeText(text).then(
      function() {
        data.copied = true;
        return;
      },
      function(err) {
        throw err;
      }
    );
  }

  return { copyLink };
}

function useOverlay(data, context) {
  function openOverlay() {
    data.showDialog = true;
    data.selectedTime = data.sharedCurrentTime;
    context.emit("pause");
  }

  function closeOverlay() {
    data.showDialog = false;
    data.copied = false;
  }

  return { openOverlay, closeOverlay };
}
</script>

<style lang="scss" scoped>
@import "../../assets/styles/main";

.custom-tooltip {
  background-color: $primary;
  color: white;
  border-radius: 3px;
  padding: 1px 4px 0 4px;
  text-align: center;
  // white-space: pre;
}
.custom-label {
  width: 60px;
  white-space: pre-wrap;
  text-align: center;
}
.custom-label.active {
  color: #3498db;
}
.black-label-text {
  &::v-deep label {
    color: #555555;
    font-size: 14px;
  }
}
.overlay-z {
  z-index: 1000 !important;
}
</style>
